
import React, { useCallback, useMemo, useRef } from 'react'
import { type NodeProps, useNodeConnections, useNodesData, useReactFlow } from '@xyflow/react'
import { useParams } from '@tanstack/react-router'
import { BatchGenerator } from '@/components/canvas/nodes/variants/BatchGenerator'
import { type GenieNodeState, NODE_DIMENSIONS } from '@genieartinc/schemas'
import { useNodeStateChange } from '@/components/canvas/hooks/useNodeStateChange'
import { NodeHandles } from '@/components/canvas/handles/NodeHandles'
import { STANDARD_HANDLES } from '@/lib/canvas/configs/handles'
import type { HandleMeta } from '@/lib/canvas/types'
import { useSeriesDataStore } from '@/stores/useSeriesDataStore'
import { useJobGeneration } from '@/hooks/useJobGeneration'
import { createImageFileNode, createVideoFileNode, createGalleryContainerNode } from '@/lib/canvas/utils/nodeFactory'
import { toReactFlowNode } from '@/lib/canvas/utils/toReactFlowNode'
import { apiCall } from '@/lib/api/api-helpers'
import { useCanvasBridgeStore } from '@/stores/useCanvasBridgeStore'
import { FEATURES } from '@/lib/features'

export interface BatchGeneratorNodeData extends Record<string, unknown> {
  state: GenieNodeState
  handles?: HandleMeta[]
  onHoverChange?: (hovered: boolean) => void
}

// Media node types → media type mapping
const MEDIA_NODE_TYPES: Record<string, 'image' | 'video' | '3d' | 'shader' | 'text'> = {
  ImageNode: 'image',
  VideoNode: 'video',
  ThreeDNode: '3d',
  ShaderNode: 'shader',
  TextNode: 'text',
}

// Media type → submitJob nodeType mapping
const MEDIA_TO_JOB_TYPE: Record<string, 'image' | 'video' | 'text'> = {
  image: 'image',
  video: 'video',
  text: 'text',
  '3d': 'image',
  shader: 'text',
}

// Media type → output file node type
const MEDIA_TO_OUTPUT_TYPE: Record<string, 'image' | 'video'> = {
  image: 'image',
  video: 'video',
  '3d': 'image',
  shader: 'image',
  text: 'image',
}

/**
 * Media types whose node is switched off behind a feature flag.
 *
 * Dropping the node from the React Flow registry only changes how it draws:
 * `node.type` stays 'ThreeDNode' in the store, so an older canvas still hands
 * one to `useNodesData` here. Ungated, its modelId would flow into submitJob
 * and spend real credits on a generator nobody is watching any more — so the
 * batch generator refuses it as a source rather than relying on the node
 * being unrenderable.
 */
const GATED_MEDIA_TYPES: readonly string[] = FEATURES.NODE_3D ? [] : ['3d']

interface ConnectedMediaConfig {
  connectedMediaType: 'image' | 'video' | '3d' | 'shader' | 'text' | null
  connectedModelId: string | null
  connectedModelName: string | null
  connectedPurpose: string | undefined
  connectedSubPurpose: string | undefined
  connectedSettings: Record<string, unknown> | undefined
  connectedNodeType: 'image' | 'video' | 'text'
  /** Set when the only upstream media node is one of the gated types. */
  unavailableMediaType: string | null
}

// Grid layout constants for output nodes
const OUTPUT_GRID = {
  cols: 5,
  nodeWidth: NODE_DIMENSIONS.ImageFile?.width ?? 500,
  gap: 20,
  offsetY: 80, // gap below batch generator
}

function BatchGeneratorNodeComponent({ id, data, selected }: NodeProps) {
  const nodeData = data as unknown as BatchGeneratorNodeData
  const { state: genieState, onHoverChange } = nodeData
  const onStateChange = useNodeStateChange(id)
  const { submitJob } = useJobGeneration()
  const { getNodes } = useReactFlow()
  const { id: canvasId } = useParams({ strict: false }) as { id?: string }

  // Track Gallery Container node ID
  const galleryContainerIdRef = useRef<string | null>(
    (genieState.galleryContainerId as string) ?? null,
  )
  // Track folder ID for batch-generated files
  const batchFolderIdRef = useRef<string | null>(
    (genieState.batchFolderId as string) ?? null,
  )

  const handleSelect = useCallback(() => {}, [])

  // Read upstream connections
  const incomingConnections = useNodeConnections({ handleType: 'target' })
  const sourceNodeIds = useMemo(
    () => incomingConnections.map((c) => c.source),
    [incomingConnections],
  )
  const sourceNodesData = useNodesData(sourceNodeIds)

  // Find upstream PromptComposer → read prompts from external store
  const promptComposerNodeId = useMemo(() => {
    for (const nodeEntry of sourceNodesData) {
      if (nodeEntry?.type === 'PromptComposer') return nodeEntry.id
    }
    return null
  }, [sourceNodesData])

  const connectedPrompts = useSeriesDataStore(
    useCallback((s) => promptComposerNodeId ? s.composedPrompts.get(promptComposerNodeId) ?? null : null, [promptComposerNodeId])
  )

  // Find upstream media node (Image, Video, etc.) → read full config
  const mediaNodeConfig = useMemo<ConnectedMediaConfig>(() => {
    let gatedMediaType: string | null = null

    for (const nodeEntry of sourceNodesData) {
      if (!nodeEntry?.type) continue
      const mediaType = MEDIA_NODE_TYPES[nodeEntry.type]
      if (!mediaType) continue
      if (GATED_MEDIA_TYPES.includes(mediaType)) {
        // Remember it so the UI can explain itself, but keep looking: the same
        // canvas may also have a working media node wired up, and that one
        // should still drive the batch.
        if (!gatedMediaType) gatedMediaType = mediaType
        continue
      }
      if (!nodeEntry.data) continue

      const sourceState = nodeEntry.data?.state as Record<string, unknown> | undefined
      return {
        connectedMediaType: mediaType,
        connectedModelId: (sourceState?.modelId as string) ?? null,
        connectedModelName: (sourceState?.modelName as string) ?? null,
        connectedPurpose: (sourceState?.purpose as string) ?? undefined,
        connectedSubPurpose: (sourceState?.subPurpose as string) ?? undefined,
        connectedSettings: (sourceState?.settings as Record<string, unknown>) ?? undefined,
        connectedNodeType: MEDIA_TO_JOB_TYPE[mediaType] || 'image',
        unavailableMediaType: null,
      }
    }

    return {
      connectedMediaType: null,
      connectedModelId: null,
      connectedModelName: null,
      connectedPurpose: undefined,
      connectedSubPurpose: undefined,
      connectedSettings: undefined,
      connectedNodeType: 'image',
      unavailableMediaType: gatedMediaType,
    }
  }, [sourceNodesData])

  // ── Gallery Container creation callback ──────────────────────────────

  const onCreateGalleryContainer = useCallback(() => {
    // Reuse existing if already created
    if (galleryContainerIdRef.current) {
      const existing = getNodes().find((n) => n.id === galleryContainerIdRef.current)
      if (existing) return galleryContainerIdRef.current
    }

    const batchNode = getNodes().find((n) => n.id === id)
    const position = {
      x: (batchNode?.position.x ?? 0) + (NODE_DIMENSIONS.BatchGenerator?.width ?? 520) + 80,
      y: batchNode?.position.y ?? 0,
    }

    const seriesName = (genieState.title as string)?.replace('Batch Generator', 'Gallery') || 'Gallery'
    const containerNode = createGalleryContainerNode(position, seriesName)

    if (containerNode.component?.state && mediaNodeConfig.connectedMediaType) {
      containerNode.component.state.mediaType = mediaNodeConfig.connectedMediaType
    }

    const rfNode = toReactFlowNode(containerNode, position)

    // Use bridge store to add nodes/edges — this updates CanvasPage's state AND saves
    const bridge = useCanvasBridgeStore.getState()
    bridge.addCanvasNodesCallback?.([rfNode])
    bridge.addCanvasEdgesCallback?.([{
      id: `edge-${id}-${containerNode.id}`,
      source: id,
      sourceHandle: 'gallery-output',
      target: containerNode.id,
      targetHandle: 'gallery-input',
      type: 'bezier',
    }])

    galleryContainerIdRef.current = containerNode.id
    onStateChange((prev: Record<string, unknown>) => ({
      ...prev,
      galleryContainerId: containerNode.id,
    }))

    return containerNode.id
  }, [id, getNodes, onStateChange, genieState.title, mediaNodeConfig.connectedMediaType])

  // ── Output node creation callback (inside Gallery Container) ────────

  const onCreateOutputNode = useCallback((
    outputUrl: string,
    index: number,
    prompt?: string,
    variationHash?: string,
  ) => {
    // Grid layout inside the container's sub-canvas
    const { cols, nodeWidth, gap } = OUTPUT_GRID
    const cellSize = nodeWidth + gap
    const position = {
      x: (index % cols) * cellSize,
      y: Math.floor(index / cols) * cellSize,
    }

    const outputType = mediaNodeConfig.connectedMediaType
      ? MEDIA_TO_OUTPUT_TYPE[mediaNodeConfig.connectedMediaType] ?? 'image'
      : 'image'

    let genieNode
    if (outputType === 'video') {
      genieNode = createVideoFileNode(position, outputUrl)
    } else {
      genieNode = createImageFileNode(position, outputUrl)
    }

    // Add batch metadata + container association to state
    if (genieNode.component?.state) {
      genieNode.component.state.galleryIndex = index
      genieNode.component.state.containerId = galleryContainerIdRef.current
      if (variationHash) genieNode.component.state.variationHash = variationHash
      if (prompt) genieNode.component.state.prompt = prompt
      genieNode.component.state.title = `#${index + 1}`
    }

    const rfNode = toReactFlowNode(genieNode, position)

    // Use bridge store — updates CanvasPage state AND auto-saves
    useCanvasBridgeStore.getState().addCanvasNodesCallback?.([rfNode])

    // Update Gallery Container's totalItems count
    if (galleryContainerIdRef.current) {
      const bridge = useCanvasBridgeStore.getState()
      bridge.updateNodeStateCallback?.(galleryContainerIdRef.current, {
        totalItems: index + 1,
      })
    }

    return genieNode.id
  }, [id, getNodes, mediaNodeConfig.connectedMediaType])

  // ── Batch folder creation callback ──────────────────────────────────

  const onCreateBatchFolder = useCallback(async (batchName: string) => {
    if (!canvasId) return null
    if (batchFolderIdRef.current) return batchFolderIdRef.current

    try {
      const result = await apiCall(`/api/canvas/${canvasId}/folders`, true, {
        method: 'POST',
        body: JSON.stringify({ name: batchName }),
      }) as { success: boolean; data?: { id: string; uuid: string } }

      if (result.success && result.data) {
        batchFolderIdRef.current = result.data.id
        onStateChange((prev: Record<string, unknown>) => ({
          ...prev,
          batchFolderId: result.data!.id,
        }))
        window.dispatchEvent(new Event('genie:file-created'))
        return result.data.id
      }
    } catch (err) {
      console.error('[BatchGenerator] Failed to create folder:', err)
    }
    return null
  }, [canvasId, onStateChange])

  // ── Move file to batch folder callback ──────────────────────────────

  const onMoveFileToBatchFolder = useCallback(async (fileUuid: string) => {
    if (!canvasId || !batchFolderIdRef.current || !fileUuid) return
    try {
      await apiCall(`/api/canvas/${canvasId}/files/${fileUuid}`, true, {
        method: 'PUT',
        body: JSON.stringify({ folderId: batchFolderIdRef.current }),
      })
      // Refresh sidebar to update folder file count
      window.dispatchEvent(new Event('genie:file-created'))
    } catch (err) {
      console.error('[BatchGenerator] Failed to move file to folder:', err)
    }
  }, [canvasId])

  // ── Reset batch folder ref ──────────────────────────────────────────

  const onResetBatchState = useCallback(() => {
    batchFolderIdRef.current = null
    galleryContainerIdRef.current = null
  }, [])

  // ── Props ────────────────────────────────────────────────────────────

  const generatorProps = useMemo(
    () => ({
      id,
      title: genieState.title,
      selected: selected || genieState.selected,
      width: NODE_DIMENSIONS.BatchGenerator.width,
      height: NODE_DIMENSIONS.BatchGenerator.height,
      onSelect: handleSelect,
      onHoverChange,
      onStateChange,
      state: genieState,
      connectedPrompts,
      connectedMediaType: mediaNodeConfig.connectedMediaType,
      connectedModelId: mediaNodeConfig.connectedModelId,
      connectedModelName: mediaNodeConfig.connectedModelName,
      connectedPurpose: mediaNodeConfig.connectedPurpose,
      connectedSubPurpose: mediaNodeConfig.connectedSubPurpose,
      connectedSettings: mediaNodeConfig.connectedSettings,
      connectedNodeType: mediaNodeConfig.connectedNodeType,
      unavailableMediaType: mediaNodeConfig.unavailableMediaType,
      submitJob,
      onCreateGalleryContainer,
      onCreateOutputNode,
      onCreateBatchFolder,
      onMoveFileToBatchFolder,
      onResetBatchState,
    }),
    [id, genieState, selected, handleSelect, onHoverChange, onStateChange, connectedPrompts, mediaNodeConfig, submitJob, onCreateGalleryContainer, onCreateOutputNode, onCreateBatchFolder, onMoveFileToBatchFolder, onResetBatchState],
  )

  const handles: HandleMeta[] = nodeData.handles || STANDARD_HANDLES.BatchGenerator

  return (
    <>
      <NodeHandles nodeId={id} handles={handles} />
      <BatchGenerator {...generatorProps} />
    </>
  )
}

export const BatchGeneratorNode = React.memo(
  BatchGeneratorNodeComponent,
  (prevProps, nextProps) => {
    const prevData = prevProps.data as unknown as BatchGeneratorNodeData
    const nextData = nextProps.data as unknown as BatchGeneratorNodeData

    return (
      prevData.state.title === nextData.state.title &&
      prevData.state.batchStatus === nextData.state.batchStatus &&
      prevData.state.progress === nextData.state.progress &&
      prevData.state.cost === nextData.state.cost &&
      prevData.state.collapsed === nextData.state.collapsed &&
      prevData.handles === nextData.handles &&
      prevProps.selected === nextProps.selected &&
      prevProps.id === nextProps.id
    )
  },
)

BatchGeneratorNode.displayName = 'BatchGeneratorNode'
